import type { StateTree } from '@makemydeal/dr-dash-types';
import { toVehicleTab, type VehicleTab, type VehicleInfo, type VehicleFuelEconomy } from '@makemydeal/dr-activities-fuel-saving';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { TradeInListItem, VehicleWithFuelEconomy, StateTreeWithVehicle } from '../types';

/**
 * Extract VINs from included trade-ins.
 * This is app-specific as it depends on dash's redux state shape.
 */
export const getTradeInVins = (allTradeIns: TradeInListItem[]): string[] => {
    return allTradeIns
        .filter((item) => item?.tradeIn?.shouldIncludeTradeIn && item?.tradeIn?.vin)
        .map((item) => item.tradeIn.vin as string);
};

/**
 * Builds fuel economy data from the init API response for a vehicle.
 * Returns null if fuel economy data is not available from init.
 */

export const buildFuelEconomyFromInit = (vehicle: VehicleWithFuelEconomy | undefined): VehicleFuelEconomy | null => {
    if (!vehicle?.vin || (!vehicle.cityFuelEconomy && !vehicle.highwayFuelEconomy && !vehicle.combinedFuelEconomy)) {
        return null;
    }

    return {
        vin: vehicle.vin,
        ok: true,
        fuelEconomy: {
            city: vehicle.cityFuelEconomy,
            highway: vehicle.highwayFuelEconomy,
            combined: vehicle.combinedFuelEconomy
        }
    };
};

/**
 * Builds fuel economy data for trade-in vehicles from init API.
 * Only includes trade-ins that have VINs and fuel economy data.
 */
export const buildTradeInFuelEconomyFromInit = (allTradeIns: TradeInListItem[]): VehicleFuelEconomy[] => {
    return allTradeIns
        .filter((item) => item?.tradeIn?.shouldIncludeTradeIn && item?.tradeIn?.vin)
        .map((item) => buildFuelEconomyFromInit(item.tradeIn as VehicleWithFuelEconomy))
        .filter((fe): fe is VehicleFuelEconomy => fe !== null);
};

/**
 * Build vehicle tabs from redux state (single vehicle).
 */
export const buildVehicleTabsFromState = (state: StateTree): VehicleTab[] => {
    const vehicle = (state as StateTreeWithVehicle).vehicle;
    const tab = toVehicleTab(vehicle, true); // Mark as primary vehicle
    return tab ? [tab] : [];
};

/**
 * Reads the primary vehicle straight from the primary scope's slice — `state.vehicle` reflects
 * whichever scope is ambiently bound, which isn't always primary.
 *
 * Fixes the "primary vehicle disappears" bug: when the slideout is opened from inside a scenario
 * scope, ambient `state.vehicle` is that scenario's vehicle, so the selling vehicle was either
 * missing from the tabs or duplicated a scenario's entry (deduped away by VIN).
 */
export const resolvePrimaryVehicleFromState = (realState: RealState): VehicleInfo | undefined => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(realState);
    const scopeState = scopedStateSelectors.getScopeStateById(realState, primaryScopeId) as any;
    return scopeState?.vehicle as VehicleInfo | undefined;
};

/**
 * Resolves non-primary scenarios from Redux scoped state.
 * Returns an array of objects with vehicle data from each non-primary scope.
 * Used by fuelSavingMiddleware to build vehicle tabs after scenario events.
 */
export const resolveScenariosFromState = (realState: RealState): Array<{ vehicle?: VehicleInfo }> => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(realState);
    const scopeIds: string[] = scopedStateSelectors.getInitializedScopesOrder(realState);
    return scopeIds
        .filter((scopeId) => scopeId !== primaryScopeId)
        .map((scopeId) => {
            const scopeState = scopedStateSelectors.getScopeStateById(realState, scopeId) as any;
            return { vehicle: scopeState?.vehicle as VehicleInfo | undefined };
        });
};

/**
 * Build vehicle tabs from state and scenarios payload (multiple vehicles).
 * Deduplicates by VIN.
 */
export const buildVehicleTabsFromScenarios = (state: StateTree, scenarios: Array<{ vehicle?: VehicleInfo }>): VehicleTab[] => {
    const tabs: VehicleTab[] = [];
    const addedVinSet = new Set<string>();

    const pushTab = (vehicle: VehicleInfo | undefined, isPrimary: boolean) => {
        const tab = toVehicleTab(vehicle, isPrimary);
        if (tab && !addedVinSet.has(tab.id)) {
            addedVinSet.add(tab.id);
            tabs.push(tab);
        }
    };

    // Primary (selling) vehicle from state.vehicle
    pushTab((state as StateTreeWithVehicle).vehicle, true);

    // Secondary vehicles from scenarios
    scenarios.forEach((scenario) => pushTab(scenario?.vehicle, false));

    return tabs;
};

/**
 * Builds vehicle tabs using scoped state as the source of non-primary scenarios.
 * Falls back to primary-only tabs when no non-primary scenarios are present.
 *
 * `resolveScopeAwarePrimaryVehicle` is the enableAutoSavingInFuelSaving gate: off keeps the old
 * behavior of trusting ambient `state.vehicle`; on resolves the primary vehicle from its own scope.
 */
export const buildVehicleTabsFromScopedState = (
    state: StateTree,
    realState: RealState,
    resolveScopeAwarePrimaryVehicle: boolean
): VehicleTab[] => {
    const nonPrimaryScenarios = resolveScenariosFromState(realState);
    // Single-scope deal: there is no other scope for `state.vehicle` to have been bound to, so it is
    // necessarily the primary and the scope-aware lookup below would be a no-op.
    if (nonPrimaryScenarios.length === 0) {
        return buildVehicleTabsFromState(state);
    }
    // Swap in the primary scope's own vehicle so the primary tab is correct regardless of which
    // scope is ambiently bound. Copies rather than mutates — `state` is the live redux state.
    const stateWithPrimaryVehicle = resolveScopeAwarePrimaryVehicle
        ? ({ ...state, vehicle: resolvePrimaryVehicleFromState(realState) } as StateTreeWithVehicle)
        : state;
    return buildVehicleTabsFromScenarios(stateWithPrimaryVehicle, nonPrimaryScenarios);
};
